import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { MapPinned, RefreshCw, Play, Leaf, CheckCircle2, Clock, Eye, PiggyBank } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { BACKEND_URL, reportError } from './adminApi';

const TYPE_LABELS = { general: 'Services à domicile', menage: 'Ménage', jardinage: 'Jardinage', 'taille-haies': 'Taille de haies' };
const POLL_MS = 12000;

/**
 * Admin card: seasonal templates (1 per page type & season) adapted to every city page.
 * Cost-efficient mode: 4 AI generations per season instead of one per city.
 */
export function AdminCityContentCard({ adminKey }) {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(null); // 'run' | page_type
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/city-content/stats?key=${adminKey}`);
      setStats(res.data);
    } catch (err) {
      reportError('contenu villes', err, () => toast.error('Impossible de charger le suivi des contenus villes'));
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const active = stats && stats.running > 0;
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [active, load]);

  const run = async () => {
    setBusy('run');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/city-content/run?key=${adminKey}`);
      toast.success('Rédaction des gabarits manquants lancée (≈ 1 min par gabarit)');
      setTimeout(load, 3000);
    } catch (err) {
      reportError('lancement gabarits', err, () => toast.error(err?.response?.data?.detail || 'Lancement impossible'));
    } finally {
      setBusy(null);
    }
  };

  const regenerate = async (pt) => {
    if (!window.confirm(`Réécrire le gabarit « ${TYPE_LABELS[pt]} » de la saison ? (1 appel IA, toutes les pages villes de ce type seront mises à jour)`)) return;
    setBusy(pt);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/city-content/${pt}/regenerate?key=${adminKey}`, null, { timeout: 180000 });
      toast.success(`Gabarit « ${TYPE_LABELS[pt]} » réécrit`);
      setPreview({ ...res.data.preview_lens, city: 'Lens', page_type: pt });
      load();
    } catch (err) {
      reportError('réécriture gabarit', err, () => toast.error(err?.response?.data?.detail || 'Réécriture impossible'));
    } finally {
      setBusy(null);
    }
  };

  const showPreview = async (pt) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/city-content/preview/${pt}/lens?key=${adminKey}`);
      setPreview({ ...res.data, page_type: pt });
    } catch (err) {
      reportError('aperçu gabarit', err, () => toast.error('Aperçu impossible'));
    }
  };

  if (!stats) {
    return <Card className="p-5 flex items-center gap-2 text-sm text-muted-foreground" data-testid="city-content-loading"><RefreshCw className="h-4 w-4 animate-spin" /> Chargement des contenus villes…</Card>;
  }

  const missing = stats.templates_total - stats.templates_ready;

  return (
    <Card className="p-5 sm:p-6" data-testid="city-content-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><MapPinned className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
          <div>
            <h3 className="font-['Space_Grotesk'] font-semibold">Contenus locaux de saison (pages villes)</h3>
            <p className="text-xs text-muted-foreground">Un gabarit IA par type de page et par saison, adapté automatiquement à chaque ville ou village. Saison en cours : <span className="font-medium capitalize" data-testid="city-content-season">{stats.season_label}</span>.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} aria-label="Actualiser" data-testid="city-content-refresh"><RefreshCw className={`h-4 w-4 ${active ? 'animate-spin' : ''}`} /></Button>
          <Button size="sm" onClick={run} disabled={busy === 'run' || missing === 0 || active} data-testid="city-content-run"><Play className="h-4 w-4 mr-1.5" /> {active ? 'Rédaction en cours…' : `Rédiger les ${missing} gabarit(s) manquant(s)`}</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="city-content-summary">
        <Badge variant="secondary" className="text-[10px]" data-testid="city-content-progress-label">{stats.templates_ready} / {stats.templates_total} gabarits prêts pour la saison</Badge>
        <Badge variant="secondary" className="text-[10px]">{stats.pages} pages villes couvertes ({stats.cities} communes)</Badge>
        <Badge variant="secondary" className="text-[10px]" data-testid="city-content-cost"><PiggyBank className="h-3 w-3 mr-1" /> ≈ {stats.estimated_cost_per_year_usd} $ / an au total</Badge>
        {stats.city_specific_texts > 0 && <Badge variant="secondary" className="text-[10px]">{stats.city_specific_texts} texte(s) spécifiques déjà rédigés conservés</Badge>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="city-content-templates">
        {Object.entries(stats.templates).map(([pt, t]) => (
          <div key={pt} className="rounded-xl border border-border p-3 flex items-start justify-between gap-3" data-testid={`city-template-${pt}`} data-ready={t.ready}>
            <div className="min-w-0">
              <p className="text-sm font-medium flex items-center gap-1.5">
                {t.ready ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))]" /> : <Clock className="h-4 w-4 text-muted-foreground" />} {TYPE_LABELS[pt] || pt}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">{t.ready ? t.titre : (active ? 'Rédaction en cours…' : 'Pas encore rédigé — texte de repli affiché')}</p>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              {t.ready && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => showPreview(pt)} aria-label="Aperçu" data-testid={`city-template-preview-${pt}`}><Eye className="h-4 w-4" /></Button>}
              <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => regenerate(pt)} disabled={!!busy} data-testid={`city-template-regen-${pt}`}>
                {busy === pt ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : (t.ready ? 'Réécrire' : 'Rédiger')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground mt-4 flex items-start gap-1.5"><Leaf className="h-3.5 w-3.5 mt-0.5 text-[hsl(var(--primary))] flex-shrink-0" /> Chaque gabarit contient plusieurs variantes d'accroche et de conclusion : deux villes n'affichent jamais exactement le même texte. À chaque changement de saison, les 4 gabarits sont réécrits automatiquement (soit 16 appels IA par an).</p>

      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="city-content-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk']">{preview?.titre}</DialogTitle>
            <DialogDescription>Aperçu tel qu'affiché sur la page « {TYPE_LABELS[preview?.page_type] || ''} » de {preview?.city || 'Lens'} · {preview?.words} mots</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            {(preview?.paragraphes || []).map((p, i) => <p key={`prev-${i}-${p.slice(0, 16)}`}>{p}</p>)}
            {preview?.conseils?.length > 0 && <ul className="list-disc pl-5 space-y-1">{preview.conseils.map((c) => <li key={c}>{c}</li>)}</ul>}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
